// Full article view — /posts/:id. Newspaper reader page: full body,
// recommend, comments with threaded replies. Back-button friendly (unlike
// the legacy modal that trapped you on the paper).

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatEventRange, offerValidity, timeAgo } from '../lib/format';
import { renderMarkdown } from '../lib/markdown';
import { EditorBar } from '../components/EditorBar';
import { PostMap } from '../components/PostMap';
import { ReviewNotes } from '../components/ReviewNotes';
import type { Post, RSVPCounts, RSVPStatus } from '../types/api';

interface Comment {
    id: string;
    parent_comment_id?: string | null;
    body: string;
    display_identity: string;
    created_at: string;
}

interface PostDetail extends Post {
    like_count?: number;
    has_liked?: boolean;
    // Public endpoint carries a comment count directly so the anon view
    // can render "N comments — sign in to read" without a second call.
    comment_count?: number;
}

export function PostView() {
    const { id }   = useParams<{ id: string }>();
    const api      = useApi();
    const navigate = useNavigate();
    const { authed, login } = useAuth();

    const [post,     setPost]     = useState<PostDetail | null>(null);
    const [comments, setComments] = useState<Comment[]>([]);
    const [error,    setError]    = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!id) return;
        try {
            if (!authed) {
                // Anon route — public post detail. Comment bodies are
                // deliberately not returned; count comes back on the post.
                const p = await api.get<PostDetail>('/api/public/posts/' + id);
                setPost(p);
                setComments([]);
                return;
            }
            const p = await api.get<PostDetail>('/api/posts/' + id);
            setPost(p);
            const c = await api.get<{ comments: Comment[] }>('/api/posts/' + id + '/comments');
            setComments(c.comments ?? []);
        } catch (e) {
            setError((e as Error).message);
        }
    }, [api, id, authed]);

    useEffect(() => { load(); }, [load]);

    if (error) return (
        <main style={{ maxWidth: 640, margin: '60px auto', padding: '0 24px' }}>
            <p style={{ color: '#8b1919' }}>Couldn't load post: {error}</p>
            <p><button type="button" className="plain" onClick={() => navigate('/')}>← Back to the paper</button></p>
        </main>
    );

    if (!post) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    async function toggleRecommend() {
        if (!post) return;
        const nextMine = !post.has_liked;
        setPost({ ...post, has_liked: nextMine, like_count: (post.like_count ?? 0) + (nextMine ? 1 : -1) });
        try {
            if (nextMine) await api.post(`/api/posts/${post.id}/recommend`);
            else          await api.del (`/api/posts/${post.id}/recommend`);
        } catch {
            // roll back
            setPost(p => p ? ({ ...p, has_liked: !nextMine, like_count: (p.like_count ?? 0) + (nextMine ? -1 : 1) }) : p);
        }
    }

    const topLevel = comments.filter(c => !c.parent_comment_id);
    const repliesFor = (parentID: string) => comments.filter(c => c.parent_comment_id === parentID);

    // Kicker rendered above the headline, matches the newspaper convention.
    let kicker = '';
    let sideBadge = '';
    if (post.post_type === 'event') {
        kicker = formatEventRange(post.starts_at, post.ends_at);
    } else if (post.post_type === 'offer') {
        if (post.price_text)  kicker    = post.price_text;
        if (post.valid_until) sideBadge = offerValidity(post.valid_until);
    } else if (post.post_type === 'notice') {
        kicker = 'Notice';
    }

    return (
        <main className={'article-view kind-' + post.post_type}>
            <div className="article-view-wrap">
                <button type="button" className="back-btn" onClick={() => navigate(-1)}>← back to the paper</button>

                {kicker && <div className="av-kicker">{kicker}</div>}
                <h1 className="av-headline">{post.title || '(untitled)'}</h1>
                {post.subhead && <p className="av-subhead">{post.subhead}</p>}
                <div className="av-byline">
                    By {post.display_identity} · {timeAgo(post.created_at)}
                    {sideBadge && <> · <span className="offer-badge">{sideBadge}</span></>}
                </div>

                {/* Author sees editor feedback on their post; hidden when none. */}
                <ReviewNotes postID={post.id} />

                {/* Editor toolkit — auto-discovers which communities the
                    caller has editor rights on for this post via
                    /api/posts/{id}/editor-context. Renders one bar per
                    community; self-hides when caller has no editor rights
                    anywhere on this post. */}
                <EditorBar postID={post.id} />

                {post.media_url && <img className="av-image" src={post.media_url} alt="" />}

                <div
                    className="av-body"
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(post.body) }}
                />

                {/* Map: any post with pins (crime watch, multi-point event)
                    renders a mini-map below the body. Single-point posts
                    (lat/lng but no pins array) also render, using lat/lng
                    as the sole pin. */}
                {post.pins && post.pins.length > 0 && (
                    <PostMap pins={post.pins} />
                )}
                {(!post.pins || post.pins.length === 0) && typeof post.lat === 'number' && typeof post.lng === 'number' && post.lat !== 0 && (
                    <PostMap pins={[{ lat: post.lat, lng: post.lng, label: post.title || undefined }]} height={200} />
                )}

                <div className="av-actions">
                    <button
                        type="button"
                        className={'recommend-btn' + (post.has_liked ? ' is-active' : '')}
                        onClick={authed ? toggleRecommend : login}
                    >
                        {post.has_liked ? '✓ ' : ''}{post.like_count ?? 0} recommend{(post.like_count ?? 0) === 1 ? '' : 's'}
                    </button>
                </div>

                {authed && post.post_type === 'event' && <RSVPPanel postID={post.id} />}

                {authed ? (
                    <section className="av-comments">
                        <h3>Comments ({comments.length})</h3>
                        {topLevel.length === 0 && <p style={{ color: '#999', fontStyle: 'italic' }}>Be the first to comment.</p>}
                        {topLevel.map(c => (
                            <CommentThread
                                key={c.id}
                                post={post}
                                comment={c}
                                replies={repliesFor(c.id)}
                                onReplyAdded={load}
                            />
                        ))}
                        <NewCommentForm postID={post.id} onCommented={load} />
                    </section>
                ) : (
                    // Signup lure — show that conversation exists but hide
                    // the bodies. This is the wall: strangers see the paper
                    // works + has real conversation, but have to join to
                    // read what neighbours actually said.
                    <section className="av-comments av-comments-locked">
                        <h3>Comments ({post.comment_count ?? 0})</h3>
                        {(post.comment_count ?? 0) > 0 ? (
                            <p className="av-comments-lure">
                                <strong>{post.comment_count} {post.comment_count === 1 ? 'neighbour has' : 'neighbours have'} something to say.</strong><br />
                                <button type="button" className="reply-btn" onClick={login}>
                                    Sign in to read the conversation →
                                </button>
                            </p>
                        ) : (
                            <p className="av-comments-lure">
                                No comments yet.{' '}
                                <button type="button" className="reply-btn" onClick={login}>Sign in</button>{' '}
                                to be the first.
                            </p>
                        )}
                    </section>
                )}
            </div>
        </main>
    );
}

interface ThreadProps {
    post:     PostDetail;
    comment:  Comment;
    replies:  Comment[];
    onReplyAdded: () => void;
}

function CommentThread({ post, comment, replies, onReplyAdded }: ThreadProps) {
    const [replying, setReplying] = useState(false);
    return (
        <div className="av-comment">
            <div className="av-comment-body">"{comment.body}"</div>
            <div className="av-comment-byline">
                — {comment.display_identity}, {timeAgo(comment.created_at)}
                {' · '}
                <button type="button" className="reply-btn" onClick={() => setReplying(v => !v)}>
                    {replying ? 'cancel' : 'reply'}
                </button>
            </div>
            {replying && (
                <NewCommentForm
                    postID={post.id}
                    parentID={comment.id}
                    onCommented={() => { setReplying(false); onReplyAdded(); }}
                />
            )}
            {replies.length > 0 && (
                <div className="av-replies">
                    {replies.map(r => (
                        <div className="av-comment" key={r.id}>
                            <div className="av-comment-body">"{r.body}"</div>
                            <div className="av-comment-byline">— {r.display_identity}, {timeAgo(r.created_at)}</div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function NewCommentForm({ postID, parentID, onCommented }: { postID: string; parentID?: string; onCommented: () => void }) {
    const api = useApi();
    const [body,     setBody]     = useState('');
    const [anon,     setAnon]     = useState(false);
    const [busy,     setBusy]     = useState(false);
    const [err,      setErr]      = useState('');

    async function onSubmit(e: FormEvent) {
        e.preventDefault();
        if (!body.trim()) return;
        setBusy(true); setErr('');
        try {
            await api.post('/api/posts/' + postID + '/comments', {
                body,
                is_anonymous: anon,
                parent_comment_id: parentID ?? null,
            });
            setBody('');
            setAnon(false);
            onCommented();
        } catch (e) {
            setErr((e as Error).message);
        }
        setBusy(false);
    }
    return (
        <form className="av-new-comment" onSubmit={onSubmit}>
            <textarea
                placeholder={parentID ? 'Your reply…' : 'Add a comment…'}
                rows={2}
                value={body}
                onChange={(e) => setBody(e.target.value)}
            />
            <div className="av-new-comment-row">
                <label style={{ fontSize: 11, color: '#666' }}>
                    <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} /> Post as anon
                </label>
                <button type="submit" className="primary" disabled={busy || !body.trim()}>
                    {parentID ? 'Reply' : 'Comment'}
                </button>
                {err && <span style={{ color: '#8b1919', fontSize: 12 }}>{err}</span>}
            </div>
        </form>
    );
}

// RSVP panel — appears under av-actions for post_type='event' only.
function RSVPPanel({ postID }: { postID: string }) {
    const api = useApi();
    const [counts,   setCounts]   = useState<RSVPCounts>({ going: 0, maybe: 0, not_going: 0, total: 0 });
    const [myStatus, setMyStatus] = useState<RSVPStatus | ''>('');

    const load = useCallback(async () => {
        try {
            const r = await api.get<{ counts: RSVPCounts; my_status: RSVPStatus | '' }>('/api/posts/' + postID + '/rsvps');
            setCounts(r.counts);
            setMyStatus(r.my_status ?? '');
        } catch { /* ignore */ }
    }, [api, postID]);
    useEffect(() => { load(); }, [load]);

    async function set(next: RSVPStatus) {
        if (myStatus === next) {
            // second click = clear
            try { await api.del('/api/posts/' + postID + '/rsvp'); } catch { /* ignore */ }
            await load();
            return;
        }
        try {
            const r = await api.post<{ counts: RSVPCounts; my_status: RSVPStatus }>(
                '/api/posts/' + postID + '/rsvp',
                { status: next },
            );
            setCounts(r.counts);
            setMyStatus(r.my_status);
        } catch { /* ignore */ }
    }

    return (
        <div className="rsvp-panel">
            <div className="rsvp-heading">Are you going?</div>
            <div className="rsvp-buttons">
                <button type="button" className={'rsvp-btn' + (myStatus === 'going'     ? ' is-active' : '')} onClick={() => set('going')}>
                    ✓ Going ({counts.going})
                </button>
                <button type="button" className={'rsvp-btn' + (myStatus === 'maybe'     ? ' is-active' : '')} onClick={() => set('maybe')}>
                    Maybe ({counts.maybe})
                </button>
                <button type="button" className={'rsvp-btn' + (myStatus === 'not_going' ? ' is-active' : '')} onClick={() => set('not_going')}>
                    Can't ({counts.not_going})
                </button>
            </div>
            <p className="rsvp-hint">
                {myStatus ? 'Tap your choice again to clear it.' : 'RSVP so others can see how full the event will be.'}
            </p>
        </div>
    );
}
